import {
	FunctionComponent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import ReactDOM from "react-dom";

import {
	differenceInDays,
	startOfMonth,
	endOfMonth,
	startOfWeek,
	endOfWeek,
	addDays,
	addMonths,
	subMonths,
	isSameMonth,
	isSameDay,
	isBefore,
	format,
} from "date-fns";

import { useTranslation } from "react-i18next";

import { faChevronDown, faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { DraftTiming, getTimingPresets } from "../utils/inboxDraft.types";
import { inviteRelativeDays } from "../utils/inviteRelativeDays.util";
import { computeTimingPopupStyle, TimingDropdownPosition } from "../utils/timingPopupPosition.util";

interface TimingDropdownProps {
	value: DraftTiming;
	onSelect: (timing: DraftTiming) => void;
	/** Vertical preference for the popup; horizontal placement always follows the viewport. */
	dropdownPosition?: TimingDropdownPosition;
	/**
	 * When set, the timing is rendered relative to a preceding LinkedIn invite:
	 * the trigger and every preset option show "N days later" (the offset from this
	 * baseline, i.e. the invite's `scheduledInDays`) instead of an absolute date.
	 * Used for invite follow-ups, whose real send date depends on when the invite
	 * is accepted. The "Now" preset is hidden in this mode (a follow-up is never
	 * sent at the same moment as the invite).
	 */
	relativeToInviteDays?: number;
	/**
	 * The message waits for the sender's pending invitation to be accepted (PREC-4216): every
	 * preset counts from the acceptance ("when they accept", "2 days after they accept"), and a
	 * calendar date, which the acceptance would move, is not offered.
	 */
	afterAcceptance?: boolean;
	/** Portal target (extension passes its shadow root); defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
}

export const TimingDropdown: FunctionComponent<TimingDropdownProps> = ({
	value,
	onSelect,
	dropdownPosition = "bottom-right",
	relativeToInviteDays,
	afterAcceptance,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const popupRef = useRef<HTMLDivElement>(null);
	const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({
		position: "fixed",
		visibility: "hidden",
		top: 0,
		left: 0,
		// Matches the measured state below, so the first (hidden) pass reports the popup's own
		// width rather than the space that happens to be left of the viewport edge.
		width: "max-content",
		zIndex: 9999,
	});
	const isRelative = relativeToInviteDays !== undefined;
	// In relative mode the invite is never followed up "now"; drop that preset.
	const timingPresets = afterAcceptance
		? getTimingPresets().filter((p) => p.id !== "custom")
		: isRelative
			? getTimingPresets().filter((p) => p.id !== "now")
			: getTimingPresets();

	// "N days later" relative to the invite baseline (used for the trigger and options).
	const relativeLabel = (scheduledInDays: number | undefined) =>
		t("inbox.draft.relativeTiming", {
			count: inviteRelativeDays(scheduledInDays, relativeToInviteDays),
		});

	// Counted from the acceptance of the pending invitation (PREC-4216).
	const acceptanceLabel = (scheduledInDays: number | undefined) =>
		scheduledInDays
			? t("inbox.pendingInvitation.daysAfterTheyAccept", { count: scheduledInDays })
			: t("inbox.pendingInvitation.whenTheyAccept");

	const optionLabel = (option: DraftTiming) => {
		if (afterAcceptance) {
			return acceptanceLabel(option.scheduledInDays);
		}

		return isRelative && option.id !== "custom"
			? relativeLabel(option.scheduledInDays)
			: option.label;
	};

	const updatePosition = useCallback(() => {
		if (!triggerRef.current || !popupRef.current) {
			return;
		}

		const rect = triggerRef.current.getBoundingClientRect();
		// Measured, not assumed: the popup is 200px wide as a preset list and 260px as the mini
		// calendar, and the right-edge clamp is only correct against the size actually rendered.
		const popup = {
			width: popupRef.current.offsetWidth,
			height: popupRef.current.offsetHeight,
		};
		const viewport = { width: window.innerWidth, height: window.innerHeight };
		const style = computeTimingPopupStyle(rect, popup, viewport, dropdownPosition);

		setPopupStyle({ ...style, visibility: "visible" });
	}, [dropdownPosition]);

	useLayoutEffect(() => {
		if (isOpen || showDatePicker) {
			updatePosition();
		} else {
			setPopupStyle((prev) => ({ ...prev, visibility: "hidden" }));
		}
	}, [isOpen, showDatePicker, updatePosition]);

	useEffect(() => {
		if (!isOpen && !showDatePicker) {
			return;
		}

		const handle = () => updatePosition();

		window.addEventListener("resize", handle);
		window.addEventListener("scroll", handle, true);

		return () => {
			window.removeEventListener("resize", handle);
			window.removeEventListener("scroll", handle, true);
		};
	}, [isOpen, showDatePicker, updatePosition]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			// Inside the extension's shadow root `event.target` is retargeted to the host, so a
			// click on one of our own options would read as outside and close the popup before
			// the option's click fires (web CLAUDE.md §11, PREC-4220).
			const target = getComposedEventTarget(event);

			if (triggerRef.current?.contains(target)) {
				return;
			}

			if (popupRef.current?.contains(target)) {
				return;
			}

			if (showDatePicker) {
				return;
			}

			setIsOpen(false);
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen, showDatePicker]);

	const handleSelect = (option: DraftTiming) => {
		if (option.id === "custom") {
			setShowDatePicker(true);
			setIsOpen(false);
		} else {
			onSelect(option);
			setIsOpen(false);
		}
	};

	const handleDateSelect = (date: Date) => {
		const today = new Date();

		today.setHours(0, 0, 0, 0);

		const days = differenceInDays(date, today);
		const day = String(date.getDate()).padStart(2, "0");
		const month = String(date.getMonth() + 1).padStart(2, "0");

		onSelect({
			id: "custom",
			label: `${day}.${month}`,
			shortLabel: `${day}.${month}`,
			value: date,
			scheduledInDays: days,
		});
		setShowDatePicker(false);
	};

	const displayLabel = afterAcceptance
		? acceptanceLabel(value.scheduledInDays)
		: isRelative
			? relativeLabel(value.scheduledInDays)
			: value.id === "now"
				? "now"
				: value.shortLabel || value.label;

	const renderPopup = () => {
		if (!isOpen && !showDatePicker) {
			return null;
		}

		const popup = (
			<div
				ref={popupRef}
				data-testid="timing-dropdown-popup"
				style={popupStyle}
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				{isOpen && (
					<div className="bg-white border border-border-light rounded-lg shadow-lg py-1 min-w-[200px]">
						{timingPresets.map((option) => (
							<button
								key={option.id}
								className={classNames(
									"w-full px-3 py-2 text-left text-sm hover:bg-brand-white flex items-center gap-2 cursor-pointer",
									value.id === option.id ? "bg-brand-white font-medium" : ""
								)}
								onClick={(e) => {
									e.stopPropagation();
									handleSelect(option);
								}}
							>
								<span>{optionLabel(option)}</span>
							</button>
						))}
					</div>
				)}
				{showDatePicker && (
					<MiniCalendar onCancel={() => setShowDatePicker(false)} onSelect={handleDateSelect} />
				)}
			</div>
		);

		if (typeof document === "undefined") {
			return popup;
		}

		return ReactDOM.createPortal(popup, portalContainer ?? document.body);
	};

	return (
		<div className="relative inline-block">
			<button
				ref={triggerRef}
				className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-blue-action font-medium border border-blue-action/20 bg-blue-action/10 hover:bg-blue-action/20 rounded transition-colors cursor-pointer"
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen(!isOpen);
				}}
			>
				<span>{displayLabel}</span>
				<Icon className="w-2 h-2 text-blue-action/60" icon={faChevronDown} />
			</button>
			{renderPopup()}
		</div>
	);
};

// ── Mini Calendar (React-based, no native date picker) ──

interface MiniCalendarProps {
	onSelect: (date: Date) => void;
	onCancel: () => void;
}

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const MiniCalendar: FunctionComponent<MiniCalendarProps> = ({ onSelect, onCancel }) => {
	const [viewDate, setViewDate] = useState(() => new Date());
	const today = useMemo(() => {
		const d = new Date();

		d.setHours(0, 0, 0, 0);

		return d;
	}, []);

	const days = useMemo(() => {
		const monthStart = startOfMonth(viewDate);
		const monthEnd = endOfMonth(viewDate);
		const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
		const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
		const result: Date[] = [];
		let day = calStart;

		while (day <= calEnd) {
			result.push(day);
			day = addDays(day, 1);
		}

		return result;
	}, [viewDate]);

	const handlePrevMonth = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		setViewDate((prev) => subMonths(prev, 1));
	}, []);

	const handleNextMonth = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		setViewDate((prev) => addMonths(prev, 1));
	}, []);

	return (
		<div className="p-3 bg-white border border-border-light rounded-lg shadow-lg w-[260px]">
			{/* Month/Year header */}
			<div className="flex items-center justify-between mb-2">
				<button
					className="p-1 hover:bg-brand-white rounded text-text-meta hover:text-text"
					type="button"
					onClick={handlePrevMonth}
				>
					<Icon className="w-3 h-3" icon={faChevronLeft} />
				</button>
				<span className="text-sm font-medium text-text">{format(viewDate, "MMMM yyyy")}</span>
				<button
					className="p-1 hover:bg-brand-white rounded text-text-meta hover:text-text"
					type="button"
					onClick={handleNextMonth}
				>
					<Icon className="w-3 h-3" icon={faChevronRight} />
				</button>
			</div>

			{/* Weekday headers */}
			<div className="grid grid-cols-7 mb-1">
				{WEEKDAYS.map((wd) => (
					<div key={wd} className="text-center text-[10px] text-text-meta font-medium py-1">
						{wd}
					</div>
				))}
			</div>

			{/* Day grid */}
			<div className="grid grid-cols-7">
				{days.map((day, i) => {
					const isCurrentMonth = isSameMonth(day, viewDate);
					const isToday = isSameDay(day, today);
					const isPast = isBefore(day, today);

					return (
						<button
							key={i}
							className={classNames(
								"text-xs py-1 rounded text-center",
								!isCurrentMonth ? "text-text-meta/40" : "",
								isCurrentMonth && !isPast ? "hover:bg-blue-action/10 text-text cursor-pointer" : "",
								isPast && isCurrentMonth ? "text-text-meta/50 cursor-not-allowed" : "",
								isToday ? "font-bold text-blue-action" : ""
							)}
							disabled={isPast}
							type="button"
							onClick={(e) => {
								e.stopPropagation();

								if (!isPast) {
									onSelect(day);
								}
							}}
						>
							{day.getDate()}
						</button>
					);
				})}
			</div>

			{/* Cancel */}
			<div className="mt-2 flex justify-end border-t border-border-light pt-2">
				<button
					className="text-xs text-text-meta hover:text-text px-2 py-1"
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onCancel();
					}}
				>
					Cancel
				</button>
			</div>
		</div>
	);
};
